// /var/www/soccer-post-operations-hub-test/src/Inbox.jsx — mentions Inbox (Eric, 2026-10-12)
// Threaded (Eric, 2026-10-22): one row per ORDER you were mentioned on, however many comments tag you.
// A row is Unread while any mention of you on the order is unread; Mark read / unread / done cover every
// mention of you on it. Opening a row shows the whole comment thread, oldest first, with the comments that
// tag you marked. A reply on the thread that does not tag you shows in the thread but never moves the row.
// Split view (Eric, 2026-10-22): on a wide screen the open row's order loads in a frame on the right half
// (the order's own page, top of the order, no sidebar); the Inbox and its thread keep the left half.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Button, Link, Checkbox, InlineStack, Banner, Page, Tabs, Text, Card, EmptyState, Spinner } from '@shopify/polaris';
import { XIcon } from '@shopify/polaris-icons';
import { useAuthFetch } from './AuthComponent';
import CommentEntry from './comments/CommentEntry';
import CommentComposer from './comments/CommentComposer';
import { plainText, tokenFor } from './comments/mentions';

const TABS = ['unread', 'read', 'done', 'all'];
const when = (iso) => {
    const d = new Date(iso), now = new Date();
    return d.toDateString() === now.toDateString()
        ? d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
        : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};
const keyOf = (it) => `${it.store_key}:${it.order_id}`;
const SPLIT_MIN_WIDTH = 1100;           // below this the order opens in a new tab instead
const refOf = (it) => ({ store_key: it.store_key, order_id: String(it.order_id) });

// The order's comment thread, loaded when the row opens. Comments that tag me carry a left accent.
function Thread({ it, storeFetch, nonce }) {
    const [state, setState] = useState({ comments: null, me: null, error: null });
    useEffect(() => {
        let alive = true;
        storeFetch(`/api/orders/${it.order_id}/comments?store=${it.store_key}`, {}, false)
            .then(async r => { if (!r.ok) throw new Error('Could not load the comments'); return r.json(); })
            .then(d => { if (alive) setState({ comments: d.comments || [], me: d.me || null, error: null }); })
            .catch(e => { if (alive) setState({ comments: [], me: null, error: e.message || 'Could not load the comments' }); });
        return () => { alive = false; };
    }, [it.order_id, it.store_key, nonce]); // eslint-disable-line
    if (state.error) return <Banner tone="critical">{state.error}</Banner>;
    if (state.comments === null) return <div className="inbox-thread__loading"><Spinner size="small" /></div>;
    const me = state.me?.user_id;
    return (
        <div className="inbox-thread">
            {state.comments.map(c => {
                const tagsMe = me != null && c.mentions && Object.prototype.hasOwnProperty.call(c.mentions, String(me));
                return (
                    <div key={c.id} className={`inbox-thread__item${tagsMe ? ' inbox-thread__item--me' : ''}`}>
                        <CommentEntry compact c={c} />
                    </div>
                );
            })}
        </div>
    );
}

export default function Inbox() {
    const storeFetch = useAuthFetch();
    const orderHref = (it) => `${window.location.origin}/store/${it.store_key}/orders/${it.order_id}#comment-${it.latest_comment_id || it.comment_id}`;
    const [tab, setTab] = useState(0);
    const [box, setBox] = useState({ items: [], counts: { unread: 0, read: 0, done: 0, all: 0 } });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selected, setSelected] = useState(new Set());
    const [page, setPage] = useState(1);
    const [open, setOpen] = useState(null);          // row key: "<store_key>:<order_id>"
    const [threadNonce, setThreadNonce] = useState(0);
    const [pane, setPane] = useState(true);          // the order frame on the right, closable
    const [frameNonce, setFrameNonce] = useState(0);
    const [wide, setWide] = useState(window.innerWidth >= SPLIT_MIN_WIDTH);
    useEffect(() => {
        const onResize = () => setWide(window.innerWidth >= SPLIT_MIN_WIDTH);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    const loadedOnce = useRef(false);
    const openRef = useRef(null);
    const itemsRef = useRef([]);
    openRef.current = open;
    itemsRef.current = box.items;

    // Spinner only on the first load; later calls refetch silently. The open row is kept in place.
    const load = useCallback(async (reset, pg = 1) => {
        if (!loadedOnce.current) setLoading(true);
        try {
            const r = await storeFetch(`/api/inbox?status=${TABS[tab]}&page=${pg}`, {}, false);
            if (!r.ok) throw new Error('Could not load the inbox');
            const d = await r.json();
            let items = d.items || [];
            const openKey = openRef.current;
            if (!reset && openKey != null && !items.some(i => keyOf(i) === openKey)) {
                const idx = itemsRef.current.findIndex(i => keyOf(i) === openKey);
                if (idx >= 0) { items = [...items]; items.splice(Math.min(idx, items.length), 0, itemsRef.current[idx]); }
            }
            if (pg > 1 || (!reset && itemsRef.current.length > items.length)) {
                const have = new Set(itemsRef.current.map(keyOf));
                items = [...itemsRef.current, ...items.filter(i => !have.has(keyOf(i)))];
            }
            setBox({ ...d, items });
            loadedOnce.current = true;
        } catch (e) {
            setError(e.message || 'Could not load the inbox');
        } finally {
            setLoading(false);
        }
    }, [tab]); // eslint-disable-line
    useEffect(() => { setOpen(null); openRef.current = null; setSelected(new Set()); setPage(1); load(true, 1); }, [load]);
    const loadMore = () => { const n = page + 1; setPage(n); load(false, n); };
    // Coming back from the frame (a comment posted on the order page) refreshes the thread and the rows.
    useEffect(() => {
        const onFocus = () => { if (openRef.current != null) { setThreadNonce(n => n + 1); load(false); } };
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [load]); // eslint-disable-line

    // Status changes update rows locally; no reload. An explicit change (Mark done / Mark read) moves the row out
    // of a tab it no longer belongs to right away (2026-10-16, Eric); the automatic unread→read on opening a row
    // keeps it in place (keepRows) until it is collapsed, so the thread does not vanish mid-read.
    const leavesTab = (status) => TABS[tab] !== 'all' && status !== TABS[tab];
    const applyLocal = (keys, status, keepRows = false) => setBox(prev => {
        const keySet = new Set(keys);
        const counts = { ...prev.counts };
        let items = prev.items.map(i => {
            if (!keySet.has(keyOf(i)) || i.status === status) return i;
            counts[i.status] = Math.max(0, (counts[i.status] || 0) - 1);
            counts[status] = (counts[status] || 0) + 1;
            return { ...i, status };
        });
        if (!keepRows && leavesTab(status)) items = items.filter(i => !keySet.has(keyOf(i)));
        return { ...prev, items, counts };
    });
    const setStatus = async (rows, status, keepRows = false) => {
        const keys = rows.map(keyOf);
        try {
            const r = await storeFetch('/api/inbox/order-status', { method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ orders: rows.map(refOf), status }) }, false);
            if (!r.ok) throw new Error('Could not update the inbox');
            applyLocal(keys, status, keepRows);
            if (!keepRows && leavesTab(status)) {
                const keySet = new Set(keys);
                setSelected(prev => new Set([...prev].filter(k => !keySet.has(k))));
                if (keySet.has(openRef.current)) setOpen(null);
                // The tab may have more rows than were loaded: top it up when the visible list runs dry.
                const left = itemsRef.current.filter(i => !keySet.has(keyOf(i))).length;
                if (left === 0 && (box.counts[TABS[tab]] || 0) - keys.length > 0) load(false, 1);
            }
            window.dispatchEvent(new Event('inbox-changed'));
        } catch (e) { setError(e.message || 'Could not update the inbox'); }
    };
    const rowsFor = (keys) => itemsRef.current.filter(i => keys.has(keyOf(i)));
    const markAllRead = async () => {
        try {
            const r = await storeFetch('/api/inbox/mark-all-read', { method: 'POST' }, false);
            if (!r.ok) throw new Error('Could not mark all read');
            applyLocal(itemsRef.current.filter(i => i.status === 'unread').map(keyOf), 'read');
            window.dispatchEvent(new Event('inbox-changed'));
        } catch (e) { setError(e.message || 'Could not mark all read'); }
    };
    const openRow = async (it) => {
        const k = keyOf(it);
        const closing = open === k;
        setOpen(closing ? null : k);
        if (!closing) setPane(true);
        if (closing) {
            // collapsing a row that no longer matches the tab drops it
            if (TABS[tab] !== 'all' && it.status !== TABS[tab]) setBox(prev => ({ ...prev, items: prev.items.filter(i => keyOf(i) !== k) }));
        } else if (it.status === 'unread') { await setStatus([it], 'read', true); }
    };
    const afterReply = () => { setThreadNonce(n => n + 1); setFrameNonce(n => n + 1); window.dispatchEvent(new Event('inbox-changed')); load(false); };
    const openItem = open != null ? box.items.find(i => keyOf(i) === open) : null;
    const split = !!openItem && wide && pane;
    const frameSrc = (it) => `/store/${it.store_key}/orders/${it.order_id}?embed=1`;
    const toggle = (k) => setSelected(prev => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });
    const allKeys = box.items.map(keyOf);
    const tabs = TABS.map(t => ({ id: t, content: `${t[0].toUpperCase()}${t.slice(1)}${t !== 'all' ? ` (${box.counts[t] || 0})` : ''}`, panelID: `${t}-panel` }));

    return (
        <Page title="Inbox" subtitle="Orders you were mentioned on" fullWidth>
          <div className={split ? 'inbox-split' : undefined}>
            <Card padding="0">
                {error && <Banner tone="critical" onDismiss={() => setError(null)}>{error}</Banner>}
                <Tabs tabs={tabs} selected={tab} onSelect={setTab} />
                <div className="inbox-toolbar">
                    <Checkbox label="Select all" labelHidden checked={selected.size > 0 && selected.size === allKeys.length}
                        indeterminate={selected.size > 0 && selected.size < allKeys.length}
                        onChange={(v) => setSelected(v ? new Set(allKeys) : new Set())} />
                    <Text as="span" tone="subdued">{selected.size ? `${selected.size} selected` : ''}</Text>
                    <Button size="slim" disabled={!selected.size} onClick={() => setStatus(rowsFor(selected), 'read')}>Mark read</Button>
                    <Button size="slim" disabled={!selected.size} onClick={() => setStatus(rowsFor(selected), 'unread')}>Mark unread</Button>
                    <Button size="slim" disabled={!selected.size} onClick={() => setStatus(rowsFor(selected), 'done')}>Mark done</Button>
                    <span style={{ flex: 1 }} />
                    <Button size="slim" onClick={markAllRead}>Mark all read</Button>
                </div>
                {loading ? <div className="inbox-empty"><Spinner size="small" /></div>
                 : !box.items.length ? <EmptyState heading={tab === 0 ? 'Nothing waiting on you' : 'Nothing here'} image="">
                        <p>When someone @mentions you on an order it shows up here and in your email.</p></EmptyState>
                 : box.items.map(it => {
                    const k = keyOf(it);
                    const isOpen = open === k;
                    const n = Number(it.comment_count || 0);
                    return (
                        <div key={k} className={`inbox-row inbox-row--${it.status}${isOpen ? ' inbox-row--open' : ''}`}>
                            <Checkbox label="Select" labelHidden checked={selected.has(k)} onChange={() => toggle(k)} />
                            <span className="inbox-row__dot" aria-label={it.status} />
                            <div className="inbox-row__main" onClick={() => openRow(it)} role="button" tabIndex={0}>
                                <InlineStack gap="200" blockAlign="center" wrap>
                                    <span onClick={(e) => e.stopPropagation()}><Link url={orderHref(it)} removeUnderline><Text as="span" fontWeight="bold">{it.order_name}</Text></Link></span>
                                    <Text as="span" tone="subdued">{it.store_key}</Text>
                                    <Badge tone={it.origin === 'shopify' ? 'success' : 'info'} size="small">{it.origin === 'shopify' ? 'Shopify' : 'OpsHub'}</Badge>
                                    {n > 1 && <Text as="span" tone="subdued" variant="bodySm">{n} comments</Text>}
                                    {it.status !== 'unread' && <Badge size="small">{it.status === 'done' ? 'Done' : 'Read'}</Badge>}
                                </InlineStack>
                                {!isOpen && <div className="inbox-row__snip"><b>{it.author_name}</b>: {plainText(it.body)}</div>}
                                {isOpen && (
                                    <div className="inbox-row__expand" onClick={(e) => e.stopPropagation()}>
                                        <Thread it={it} storeFetch={storeFetch} nonce={threadNonce} />
                                        <CommentComposer orderId={it.order_id} storeFetch={(url, opts) => storeFetch(`${url}${url.includes('?') ? '&' : '?'}store=${it.store_key}`, opts, false)}
                                            prefill={it.mention_author_user_id ? { token: tokenFor({ user_id: it.mention_author_user_id, name: it.mention_author_name }), nonce: k } : null}
                                            replyToMentionId={it.mention_id}
                                            onPosted={afterReply} />
                                        <InlineStack gap="200" blockAlign="center" wrap>
                                            <Button size="slim" url={orderHref(it)} external>Open order</Button>
                                            {it.status !== 'done' && <Button size="slim" onClick={() => setStatus([it], 'done')}>Mark done</Button>}
                                            {/* 2026-10-16 (Eric): a read (or done) row can be put back to Unread from here */}
                                            {it.status !== 'unread' && <Button size="slim" onClick={() => setStatus([it], 'unread')}>Mark unread</Button>}
                                            {wide && !pane && <Button size="slim" onClick={() => setPane(true)}>Show order here</Button>}
                                        </InlineStack>
                                    </div>
                                )}
                            </div>
                            <span className="inbox-row__when">{when(it.created_at)}</span>
                        </div>
                    );
                })}
                {!loading && box.items.length > 0 && box.items.length >= page * (box.per_page || 50) && (
                    <div className="inbox-toolbar"><Button onClick={loadMore}>Load more</Button></div>
                )}
            </Card>
            {split && (
                <div className="inbox-pane">
                    <div className="inbox-pane__bar">
                        <Text as="span" fontWeight="semibold">{openItem.order_name}</Text>
                        <Text as="span" tone="subdued">{openItem.store_key}</Text>
                        <span style={{ flex: 1 }} />
                        <Button size="slim" variant="plain" url={orderHref(openItem)} external>Open in a tab</Button>
                        <Button size="slim" variant="tertiary" icon={XIcon} accessibilityLabel="Close the order" onClick={() => setPane(false)} />
                    </div>
                    <iframe key={`${open}:${frameNonce}`} className="inbox-pane__frame" src={frameSrc(openItem)} title={`Order ${openItem.order_name}`} />
                </div>
            )}
          </div>
        </Page>
    );
}
